import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../styles/pages/notifications/InsightItemConfig.scss';
import '../../../styles/pages/notifications/reportsPresentations/presentationExecutionHistory.scss';
import { clockSVG, bellSVG } from '../../../lib/allCustomSVGs';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../../ui/noData/NoData';
import PaginationMain from '../../../components/pagination/PaginationMain';

import get_notifications_execution_history_api_call, {
  NotificationsExecutionHistoryItem,
  NotificationsExecutionHistoryPagination,
} from '../../../lib/api/Reports/notifications/notifications-history-api-call';

type Props = {
  togglePopupModal: (isOpen: boolean) => void;
};

type LoadState = 'pending' | 'success' | 'error' | 'null';

const ITEMS_PER_PAGE = 5; // fixed page size

const getStatusInfo = (status: string) => {
  // Validate input
  if (!status || typeof status !== 'string' || status.trim() === '') {
    return { className: 'other', label: 'Unknown' };
  }

  // Determine CSS class: success, failed, or other
  let className = 'other';
  const statusLower = status.toLowerCase();

  if (statusLower === 'completed') {
    className = 'success';
  } else if (statusLower === 'failed') {
    className = 'failed';
  } else if (statusLower === 'processing') {
    className = 'other'; // or 'processing' if you have a specific style
  } else if (statusLower === 'queued') {
    className = 'other';
  }

  // Clean and format label: remove dashes, underscores, etc.
  const label = status.replace(/[-_]/g, ' ');

  return { className, label };
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso || typeof iso !== 'string') {
    return '';
  }

  try {
    const d = new Date(iso);
    // Check if date is valid
    if (isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const formatBatchIdentifier = (
  batchIdentifier: string | null | undefined
): string => {
  if (!batchIdentifier || typeof batchIdentifier !== 'string') {
    return 'Unknown Batch';
  }

  try {
    const trimmed = batchIdentifier.trim();
    if (!trimmed) {
      return 'Unknown Batch';
    }

    // Handle v2 format: v2_176_2025-11-17_13-04-22
    if (trimmed.startsWith('v2_')) {
      const parts = trimmed.split('_');
      if (parts.length >= 4 && parts[2] && parts[3]) {
        const version = parts[0] || 'v2';
        const date = parts[2];
        const time = parts[3]?.replace(/-/g, ':') || '';
        if (date && time) {
          return `${version.toUpperCase()} • ${date} • ${time}`;
        }
        if (date) {
          return `${version.toUpperCase()} • ${date}`;
        }
      }
    }

    // Handle insights format: insights_176_20250819_000021
    if (trimmed.startsWith('insights_')) {
      const parts = trimmed.split('_');
      if (parts.length >= 3 && parts[2]) {
        const prefix = parts[0] || 'insights';
        const dateStr = parts[2];
        // Format date: 20250819 -> 2025-08-19
        if (dateStr && dateStr.length === 8) {
          try {
            const formattedDate = `${dateStr.substring(
              0,
              4
            )}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
            if (parts[3]) {
              const timeStr = parts[3];
              // Format time: 000021 -> 00:00:21
              if (timeStr && timeStr.length === 6) {
                const formattedTime = `${timeStr.substring(
                  0,
                  2
                )}:${timeStr.substring(2, 4)}:${timeStr.substring(4, 6)}`;
                return `${prefix} • ${formattedDate} • ${formattedTime}`;
              }
            }
            return `${prefix} • ${formattedDate}`;
          } catch {
            // If date parsing fails, continue to fallback
          }
        }
      }
    }

    // Fallback: return formatted version with underscores replaced
    return trimmed.replace(/_/g, ' • ');
  } catch (error) {
    console.error('Error formatting batch identifier:', error);
    return batchIdentifier;
  }
};

const formatPlatformsFetched = (
  platforms: string[] | undefined | null
): string => {
  if (!platforms || !Array.isArray(platforms) || platforms.length === 0) {
    return '';
  }

  try {
    return platforms
      .filter((p) => p && typeof p === 'string')
      .map((p) => {
        try {
          return p.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        } catch {
          return p;
        }
      })
      .join(', ');
  } catch (error) {
    console.error('Error formatting platforms fetched:', error);
    return '';
  }
};

const formatMetadataInfo = (
  metadata: NotificationsExecutionHistoryItem['metadata'],
  t: (key: string) => string
): string => {
  if (!metadata || typeof metadata !== 'object') {
    return '';
  }

  try {
    const parts: string[] = [];

    if (
      typeof metadata.insights_count === 'number' &&
      !isNaN(metadata.insights_count)
    ) {
      parts.push(`${metadata.insights_count} insights`);
    }

    if (
      typeof metadata.graphs_count === 'number' &&
      !isNaN(metadata.graphs_count)
    ) {
      parts.push(`${metadata.graphs_count} graphs`);
    }

    if (
      typeof metadata.prompts_count === 'number' &&
      !isNaN(metadata.prompts_count)
    ) {
      parts.push(`${metadata.prompts_count} prompts`);
    }

    const platforms = formatPlatformsFetched(metadata.platforms_fetched);
    if (platforms) {
      parts.push(`Platforms: ${platforms}`);
    }

    if (metadata.version && typeof metadata.version === 'string') {
      parts.push(`Version: ${metadata.version}`);
    }

    if (
      metadata.processing_method &&
      typeof metadata.processing_method === 'string'
    ) {
      parts.push(`Processing: ${metadata.processing_method}`);
    }

    if (
      metadata.processing_mode &&
      typeof metadata.processing_mode === 'string'
    ) {
      parts.push(`Mode: ${metadata.processing_mode}`);
    }

    if (metadata.n8n_delivery_triggered === true) {
      parts.push(t('Delivery Triggered'));
    }

    return parts.join(' • ');
  } catch (error) {
    console.error('Error formatting metadata info:', error);
    return '';
  }
};

const NotificationsHistory = ({ togglePopupModal }: Props) => {
  const { t } = useTranslation();

  const [items, setItems] = useState<NotificationsExecutionHistoryItem[]>([]);
  const [pagination, setPagination] =
    useState<NotificationsExecutionHistoryPagination | null>(null);
  const [loading, setLoading] = useState<LoadState>('pending');

  // 0-based page index for PaginationMain (mirrors your reference)
  const [currentPage, setCurrentPage] = useState<number>(0);

  // Guards against loops / double invokes
  const didInitRef = useRef(false);
  const inFlightRef = useRef(false);

  const fetchHistory = async (zeroBasedPage: number) => {
    // Prevent re-entrant / rapid double calls
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setLoading('pending');
    try {
      const apiPage = zeroBasedPage + 1; // API expects 1-based
      const res = await get_notifications_execution_history_api_call(
        apiPage,
        ITEMS_PER_PAGE
      );

      if (res && Array.isArray(res.items)) {
        // Validate and sanitize items - show items with minimal required data
        // Only require 'id' to prevent filtering out valid items
        const validItems = res.items.filter(
          (item) => item && typeof item === 'object' && item.id // Only require ID, everything else is optional
        );
        setItems(validItems);
        setPagination(res.pagination || null);
        setLoading(validItems.length > 0 ? 'success' : 'null');
      } else {
        setItems([]);
        setPagination(null);
        setLoading('null');
      }
    } catch (e) {
      console.error('Error fetching notifications execution history:', e);
      setItems([]);
      setPagination(null);
      setLoading('error');
    } finally {
      inFlightRef.current = false;
    }
  };

  // Fetch once per mount (like your reference modal popups)
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchHistory(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty to avoid effect churn

  const pageCount = useMemo(() => {
    const lastPage = pagination?.last_page;
    if (typeof lastPage === 'number' && lastPage > 0 && !isNaN(lastPage)) {
      return lastPage;
    }
    return 1;
  }, [pagination?.last_page]);

  const totalRecords = useMemo(() => {
    const total = pagination?.total;
    if (typeof total === 'number' && total >= 0 && !isNaN(total)) {
      return total;
    }
    return 0;
  }, [pagination?.total]);

  const handlePageClick = (event: { selected: number | undefined }) => {
    const next = typeof event?.selected === 'number' ? event.selected : 0;
    if (next === currentPage) return; // no-op if same page
    if (next < 0) return; // Prevent negative page numbers
    setCurrentPage(next);
    fetchHistory(next);
  };

  return (
    <div style={{ width: '100%' }} className='insightItemConfig'>
      <div className='insightItemConfig__title'>
        <div className='insightItemConfig__title--main'>
          <h3>{t('NotificationsExecutionHistory')}</h3>
        </div>
        <p>{t('NotificationsExecutionHistoryDescription')}</p>
      </div>

      {loading === 'pending' && (
        <div className='containerWithPaddingTopOnMobile'>
          <LoadingSpinner />
        </div>
      )}

      {loading === 'error' && (
        <div className='containerWithPaddingTopOnMobile'>
          <NoData
            title={t('ErrorFetchingData')}
            subTitle={t('SomethingWentWrong-msg')}
          />
        </div>
      )}

      {loading === 'null' && (
        <div className='containerWithPaddingTopOnMobile'>
          <NoData
            title={t('NoHistoryFound')}
            subTitle={t('NoNotificationsHistoryDescription')}
          />
        </div>
      )}

      {loading === 'success' && (
        <>
          <div className='presentationExecutionHistory'>
            {Array.isArray(items) && items.length > 0 ? (
              items.map((it) => {
                // Only require ID - show item with whatever data is available
                if (!it || !it.id) {
                  return null;
                }

                const { className: tagClass, label: tagText } = getStatusInfo(
                  it.status || 'unknown'
                );

                const batchId = formatBatchIdentifier(
                  it.batch_identifier || it.id
                );
                // Use executed_at if available, otherwise use executed_date
                const executedAt = formatDateTime(
                  it.executed_at || it.executed_date
                );

                return (
                  <div
                    key={it.id}
                    className={`presentationExecutionHistory__item ${
                      tagClass === 'failed' ? 'failed' : ''
                    }`}
                  >
                    <div className='presentationExecutionHistory__item--main'>
                      <div className='presentationExecutionHistory__item--main-left'>
                        <div className='presentationExecutionHistory__item--main-left-title'>
                          <CustomSVGs svg={bellSVG} />
                          <h3>{batchId}</h3>
                          {tagText && (
                            <div
                              className={`presentationExecutionHistory__item--main-left-title-tag ${tagClass}`}
                            >
                              <span>{tagText}</span>
                            </div>
                          )}
                        </div>

                        <div className='presentationExecutionHistory__item--main-left-description'>
                          <CustomSVGs svg={clockSVG} />
                          <span>
                            {executedAt ||
                              (it.status === 'processing'
                                ? t('Processing...')
                                : t('Pending'))}
                          </span>
                        </div>

                        <div className='presentationExecutionHistory__item--main-left-info'>
                          {formatMetadataInfo(it.metadata, t)}
                        </div>
                      </div>

                      <div className='presentationExecutionHistory__item--main-right'></div>
                    </div>

                    {it.status === 'failed' &&
                      it.error_message &&
                      typeof it.error_message === 'string' &&
                      it.error_message.trim() && (
                        <div className='presentationExecutionHistory__item--errorMessage'>
                          <span>{it.error_message}</span>
                        </div>
                      )}
                  </div>
                );
              })
            ) : (
              <div className='containerWithPaddingTopOnMobile'>
                <NoData
                  title={t('NoHistoryFound')}
                  subTitle={t('NoNotificationsHistoryDescription')}
                />
              </div>
            )}
          </div>

          {/* Pagination — exactly like your reference */}
          {totalRecords > 0 &&
            pageCount > 0 &&
            typeof currentPage === 'number' &&
            !isNaN(currentPage) && (
              <div
                className='shortcodesPopup__pagination'
                style={{ marginTop: '1.25rem' }}
              >
                <PaginationMain
                  key='notifications-history-pagination'
                  totalRecords={totalRecords}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onRecordsPerPageChange={() => {}}
                  breakLabel='...'
                  nextLabel={t('Next >')}
                  onPageChange={handlePageClick}
                  pageRangeDisplayed={2}
                  initialPage={Math.max(0, currentPage)}
                  pageCount={pageCount}
                  previousLabel={t('< Previous')}
                  marginPagesDisplayed={2}
                  containerClassName='pagination'
                  pageLinkClassName='pagination__page-num'
                  previousLinkClassName='pagination__page-num'
                  nextLinkClassName='pagination__page-num'
                  disabledLinkClassName='pagination__page-num-disabled'
                  activeLinkClassName='pagination__active'
                />
              </div>
            )}
        </>
      )}

      <div
        style={{ marginTop: '2.5rem' }}
        className='insightItemConfig__buttons'
      >
        <MainSquareButton
          value={t('Close')}
          type='button'
          buttonType='white'
          disabled={false}
          onClick={() => togglePopupModal(false)}
        />
      </div>
    </div>
  );
};

export default NotificationsHistory;
